import React, { useState } from 'react';
import axios from 'axios';
import {
  Box, Typography, Paper, TextField, Button, Grid, Card, CardContent,
  Alert, FormControl, InputLabel, Select, MenuItem, Divider, CircularProgress, Chip
} from '@mui/material';
import { Search as SearchIcon, Save as SaveIcon } from '@mui/icons-material';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/reactApi/api';

const SIZE_OPTIONS = [
  { label: 'Micro Laboratories (Up to 25 patients/ day)',    value: 'Micro Laboratories (Up to 25 patients/ day)',    flagKey: 'smallLab' },
  { label: 'Mini Laboratories (26 - 50 patients/ day)',      value: 'Mini Laboratories (26 - 50 patients/ day)',      flagKey: 'smallLab' },
  { label: 'Small Laboratories (51-100 patients/ day)',      value: 'Small Laboratories',                            flagKey: 'smallLab' },
  { label: 'Medium Laboratories (101-400 patients/ day)',    value: 'Medium Laboratories',                           flagKey: 'mediumLab' },
  { label: 'Large Laboratories (401-1000 patients/ day)',    value: 'Large Laboratories',                            flagKey: 'largeLab' },
  { label: 'Very Large Laboratories (Above 1000 patients/ day)', value: 'Very Large Laboratories',                  flagKey: 'veryLargeLab' },
];

const sizeToFlags = (size) => {
  const opt = SIZE_OPTIONS.find(o => o.value === size);
  const flagKey = opt?.flagKey || 'smallLab';
  return {
    smallLab:     flagKey === 'smallLab'     ? 1 : 0,
    mediumLab:    flagKey === 'mediumLab'    ? 1 : 0,
    largeLab:     flagKey === 'largeLab'     ? 1 : 0,
    veryLargeLab: flagKey === 'veryLargeLab' ? 1 : 0,
  };
};

const ChangeCabSize = () => {
  const [username, setUsername] = useState('');
  const [applications, setApplications] = useState([]);
  const [selectedAppId, setSelectedAppId] = useState('');
  const [labDetails, setLabDetails] = useState(null);
  const [selectedSize, setSelectedSize] = useState('');
  const [collectionCenters, setCollectionCenters] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchingDetails, setFetchingDetails] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSearchUser = async () => {
    if (!username.trim()) { setError('Enter a username'); return; }
    setLoading(true);
    setError('');
    setApplications([]);
    setSelectedAppId('');
    setLabDetails(null);
    try {
      const res = await axios.get(`${API_BASE_URL}/common/applications-with-type/${username.trim()}`);
      if (res.data.success && res.data.data?.length > 0) {
        setApplications(res.data.data);
      } else {
        setError('No applications found for this username');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error searching applications');
    } finally {
      setLoading(false);
    }
  };

  const handleAppSelect = async (appId) => {
    setSelectedAppId(appId);
    setLabDetails(null);
    setSelectedSize('');
    setCollectionCenters('');
    setError('');
    setSuccess('');
    if (!appId) return;
    setFetchingDetails(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/fees/application/${appId}`);
      if (res.data?.data?.labDetails) {
        const details = res.data.data.labDetails;
        setLabDetails(details);
        setSelectedSize(details.LabCategory || '');
        setCollectionCenters(details.NumberOfCollectionCenters?.toString() || '0');
      } else {
        setError('No lab details found for this application');
      }
    } catch (err) {
      setError('Error fetching lab details');
    } finally {
      setFetchingDetails(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedSize) { setError('Select a lab size'); return; }
    if (!selectedAppId || !labDetails?.OrgId) { setError('Select an application first'); return; }
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await axios.put(`${API_BASE_URL}/ac-process/lab/cab-size-direct`, {
        appId: selectedAppId,
        accreditationCategory: selectedSize,
        numberOfCollectionCenters: parseInt(collectionCenters) || 0,
      });
      if (res.data.success) {
        setSuccess(`CAB size updated to "${selectedSize}" successfully`);
        setLabDetails(prev => ({ ...prev, LabCategory: selectedSize, NumberOfCollectionCenters: parseInt(collectionCenters) || 0 }));
      } else {
        setError(res.data.message || 'Update failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error updating lab size');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, color: '#00024c', mb: 3 }}>
        Change CAB / Lab Size
      </Typography>

      {/* Step 1: Search */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Step 1: Search Laboratory
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearchUser()}
              fullWidth
              size="small"
              placeholder="e.g. Nabln000060"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
              onClick={handleSearchUser}
              disabled={loading}
              sx={{ bgcolor: '#00024c', '&:hover': { bgcolor: '#000380' }, height: 40 }}
            >
              Search
            </Button>
          </Grid>
        </Grid>

        {applications.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Select Application</InputLabel>
              <Select
                value={selectedAppId}
                label="Select Application"
                onChange={(e) => handleAppSelect(e.target.value)}
              >
                <MenuItem value="">-- Select --</MenuItem>
                {applications.map((app) => (
                  <MenuItem key={app.AppId} value={app.AppId}>
                    {app.ApplicationTypeName ? ` — ${app.ApplicationTypeName}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </Paper>

      {/* Step 2: Current details + edit form */}
      {fetchingDetails && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {labDetails && (
        <>
          {/* Current details card */}
          <Card elevation={2} sx={{ mb: 3, borderLeft: '4px solid #00024c' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                Current Lab Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">CAB Name</Typography>
                  <Typography variant="body1" fontWeight={500}>{labDetails.CabName || '—'}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Current Size</Typography>
                  <Chip
                    label={labDetails.LabCategory || 'Not Set'}
                    color={labDetails.LabCategory ? 'primary' : 'default'}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Collection Centers</Typography>
                  <Typography variant="body1" fontWeight={500}>{labDetails.NumberOfCollectionCenters ?? '—'}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">CAB Type</Typography>
                  <Typography variant="body1" fontWeight={500}>{labDetails.CabType || '—'}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Edit form */}
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Step 2: Update CAB Size
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={5}>
                <FormControl fullWidth size="small">
                  <InputLabel>New Lab Size *</InputLabel>
                  <Select
                    value={selectedSize}
                    label="New Lab Size *"
                    onChange={(e) => setSelectedSize(e.target.value)}
                  >
                    <MenuItem value="">-- Select --</MenuItem>
                    {SIZE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="No. of Collection Centers"
                  type="number"
                  value={collectionCenters}
                  onChange={(e) => setCollectionCenters(e.target.value)}
                  fullWidth
                  size="small"
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  onClick={handleSubmit}
                  disabled={loading || !selectedSize}
                  sx={{ bgcolor: '#E34124', '&:hover': { bgcolor: '#c73518' }, height: 40, px: 3 }}
                >
                  Update
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </>
      )}

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
    </Box>
  );
};

export default ChangeCabSize;